import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Compass,
  ShieldCheck,
  Users,
  Layers,
  GraduationCap,
} from "lucide-react";
import { COMPANY, IMG } from "@/lib/constants";
import { CTASection } from "@/components/CTASection";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";

const aboutImage = IMG.engineerOutdoor;

const services = [
  "Electrical Panel Design",
  "Panel Manufacturing",
  "Installation & Commissioning",
  "Testing & Maintenance",
];

const strengths = [
  {
    icon: Layers,
    title: "Advanced Engineering Expertise",
    desc: "LT & MV panel design rooted in industry-driven practice.",
  },
  {
    icon: ShieldCheck,
    title: "Strict Quality & Compliance",
    desc: "Every panel validated against safety and performance standards.",
  },
  {
    icon: Target,
    title: "Customized, Scalable Solutions",
    desc: "Systems designed to match today's load and tomorrow's expansion.",
  },
  {
    icon: Users,
    title: "Client-Centric Approach",
    desc: "Fast turnaround, transparent communication, reliable delivery.",
  },
  {
    icon: GraduationCap,
    title: "Integrated Training",
    desc: "Bridging academic learning with real-world engineering practice.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] flex items-center overflow-hidden bg-navy-950 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${IMG.engineerTablet}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,179,8,0.18),transparent_55%)]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 w-full pt-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT — main content */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#EAB308]">
                <span className="h-px w-8 bg-[#EAB308]" />
                About Prime Volt
              </div>

              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                Engineering Excellence.{" "}
                <br className="hidden sm:inline" />
                <span className="text-[#EAB308]">Powering</span> Reliable Solutions.
              </h1>

              <p className="mt-5 max-w-xl text-zinc-300 leading-relaxed">
                Your trusted partner for electrical solutions — delivering
                high-quality panel design, manufacturing, installation and
                maintenance for industries that never pause.
              </p>
              
              {/* MOBILE stats strip (since right card is hidden on mobile) */}
              <div className="mt-8 lg:hidden grid grid-cols-2 border border-zinc-800 divide-x divide-zinc-800 bg-zinc-900/60 backdrop-blur">
                <div className="px-2 py-3 text-center">
                  <div className="text-lg font-black tracking-tighter text-[#EAB308]">100%</div>
                  <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Quality Assured</div>
                </div>
                <div className="px-2 py-3 text-center">
                  <div className="text-lg font-black tracking-tighter text-[#EAB308]">24/7</div>
                  <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Reliability</div>
                </div>
              </div>
            </div>

            {/* RIGHT — custom creative element matching home page style */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                {/* Reversed accent boxes to distinguish from home slightly, but same aesthetic */}
                <div className="absolute -top-3 -right-3 h-20 w-20 border-2 border-[#EAB308] z-0" />
                <div className="absolute -bottom-3 -left-3 h-20 w-20 bg-[#EAB308]/90 z-0" />

                <div className="relative bg-navy-900/80 backdrop-blur-xl border border-navy-800">
                  <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div className="text-xs font-black uppercase tracking-[0.3em] text-[#EAB308]">
                      Our Core Purpose
                    </div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Prime Volt Eng.
                    </div>
                  </div>

                  <div className="p-6 border-b border-zinc-800">
                    <div className="text-sm font-bold text-white leading-relaxed">
                      "To build electrical systems that stand the test of time, load, and safety — bridging the gap between rigorous engineering and real-world reliability."
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-zinc-800">
                    <div className="px-3 py-4 text-center">
                      <div className="text-xl font-black tracking-tighter text-[#EAB308]">
                        100%
                      </div>
                      <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
                        Quality Assured
                      </div>
                    </div>
                    <div className="px-3 py-4 text-center">
                      <div className="text-xl font-black tracking-tighter text-[#EAB308]">
                        24/7
                      </div>
                      <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
                        Reliability
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* scroll hint just like home page */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 font-bold">
          <span>Scroll</span>
          <span className="h-6 w-px bg-gradient-to-b from-[#EAB308] to-transparent" />
        </div>
      </section>

      {/* Intro grid with image */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-24 w-24 bg-[#EAB308] z-0" />
              <img
                src={aboutImage}
                alt="Engineer working on an electrical panel"
                className="relative z-10 w-full h-[460px] md:h-[560px] object-cover transition-all duration-700"
              />
              <div className="absolute -bottom-4 -right-4 h-24 w-24 border-4 border-navy-950 z-0" />
            </div>

            <div>
              <EyebrowLabel>Who We Are</EyebrowLabel>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-950 leading-[1.05]">
                Forward-thinking electrical engineering.
              </h2>
              <div className="mt-6 space-y-5 text-zinc-700 leading-relaxed">
                <p>
                  Prime Volt Engineering Services is a forward-thinking
                  engineering company specializing in the design and development
                  of high-performance electrical panels — including MCC, PCC,
                  PMCC, APFC, and Control Panels.
                </p>
                <p>
                  With a strong foundation in engineering expertise and
                  industry-driven practices, we deliver solutions that meet the
                  highest standards of safety, reliability, and efficiency. Our
                  approach combines technical excellence with a deep
                  understanding of client requirements.
                </p>
                <p>
                  Beyond engineering, we actively contribute to skill
                  development by offering practical training programs that
                  bridge the gap between academic learning and real-world
                  industry requirements.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-0 border border-zinc-200">
                {services.map((s, i) => (
                  <div
                    key={s}
                    className={`p-5 flex items-start gap-3 ${
                      i % 2 === 0 ? "border-r border-zinc-200" : ""
                    } ${i < 2 ? "border-b border-zinc-200" : ""}`}
                  >
                    <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#EAB308] shrink-0" />
                    <span className="text-sm font-semibold text-zinc-950">
                      {s}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 border-l-2 border-[#EAB308] pl-4 py-1">
                SLD, Layout & Control Circuits · MCC, PCC, PMCC, APFC Panels
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="relative bg-navy-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url('${IMG.transmission}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/95 to-navy-950" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-0 border border-navy-800">
            <div className="p-10 md:p-14 border-b md:border-b-0 md:border-r border-navy-800">
              <Compass className="h-10 w-10 text-[#EAB308]" strokeWidth={1.5} />
              <div className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                Our Vision
              </div>
              <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                To become a trusted leader in electrical engineering solutions.
              </h3>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Consistently delivering innovation, quality, and excellence
                across every panel we design and every project we power.
              </p>
            </div>
            <div className="p-10 md:p-14">
              <Target className="h-10 w-10 text-[#EAB308]" strokeWidth={1.5} />
              <div className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                Our Mission
              </div>
              <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                Reliable, efficient and cost-effective electrical solutions.
              </h3>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Maintaining the highest standards of engineering integrity and
                customer satisfaction on every engagement — big or small.
              </p>
            </div>
          </div>

          <div className="mt-16 max-w-4xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
              Closing Line
            </div>
            <blockquote className="mt-4 text-2xl md:text-4xl font-black tracking-tighter leading-tight">
              "At Prime Volt Engineering Services, we don't just build panels —
              we <span className="text-[#EAB308]">power industries</span> with
              confidence."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Key Strengths */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <SectionHeading
            eyebrow="Key Strengths"
            title="What makes us different."
            subtitle="Five pillars that shape every project we deliver."
          />

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200">
            {strengths.map((s, i) => (
              <div
                key={s.title}
                data-testid={`strength-${i}`}
                className={`p-8 md:p-10 hover:bg-zinc-50 transition-colors ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-zinc-200" : ""
                } ${i % 2 === 0 ? "md:border-r lg:border-r border-zinc-200" : ""} ${
                  i < strengths.length - (strengths.length % 3 || 3)
                    ? "border-b border-zinc-200"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <s.icon
                    className="h-10 w-10 text-zinc-950"
                    strokeWidth={1.5}
                  />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-black tracking-tight text-zinc-950">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 bg-navy-950 hover:bg-[#EAB308] hover:text-navy-950 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-navy-950"
            >
              Learn More About Us
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="about-call"
              className="inline-flex items-center gap-2 bg-transparent hover:bg-navy-950 hover:text-white text-navy-950 px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-navy-950"
            >
              Call {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
